import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {batch, useDispatch} from 'react-redux';
import PipettingProfileWrapper from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileWrapper';
import PipettingProfileItems from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileItem/PipettingProfileItems';
import {
  setTaskMixingPositionAspirate,
  setTaskMixingPositionDispense,
  setTaskTipPosition,
  setTaskEnableMixing,
  setTaskMixingCycle,
  setTaskMixingVolume,
  setTaskSpeed,
  setTaskRetractSpeed,
  setTaskMixingSpeed,
  setTaskTipTouchOff,
  setTaskTipTouchOffHeight,
  setTaskTipTouchOffDirectionX,
  setTaskTipTouchOffDirectionY,
  setTaskTipTouchOffDelay,
} from 'stores/actions/cardStateAction';
import PipettingProfileExpanderButton from 'contents/ProtocolEditor/TaskList/PipettingProfile/PipettingProfileExpanderButton';
import {updateRunningProtocol} from 'stores/business/protocolManagementBusiness';
import {globalAddUndoItem} from 'stores/business/undoRedoBusinessAction';
import {TaskType} from 'constants/dataEnum';
import {updatePipettingTipPositionOffset} from 'stores/business/pipettingParameterBusiness';

function SerialDilutionAliquotPipettingProfile({tasks, runtimeMode}) {
  const dispatch = useDispatch();

  const [isPipettingProfileExpanded, setIsPipettingProfileExpanded] = useState(false);

  const {[TaskType.ASPIRATE]: aspirateTasks} = tasks || {};
  const sampleAspirateTask = aspirateTasks && aspirateTasks[0];

  const postUpdateAction = () => {
    if (runtimeMode) {
      dispatch(updateRunningProtocol());
    } else {
      dispatch(globalAddUndoItem());
    }
  };

  const getAffectedTaskIdsAndUpdateValue = (updateFunction, value) => {
    batch(() => {
      aspirateTasks.forEach((task) => dispatch(updateFunction(task.ID, value)));
      postUpdateAction();
    });
  };

  const updateAspirateOfPipettingTipPositionOffset = (direction, value) => {
    batch(() => {
      aspirateTasks.forEach((task, index) => {
        dispatch(updatePipettingTipPositionOffset(direction, value, task, index !== 0));
      });
      postUpdateAction();
    });
  };

  const setTipPosition = (value) => getAffectedTaskIdsAndUpdateValue(setTaskTipPosition, value);
  const setTipXPosition = (value) => updateAspirateOfPipettingTipPositionOffset('x', value);
  const setTipYPosition = (value) => updateAspirateOfPipettingTipPositionOffset('y', value);
  const setSpeed = (value) => getAffectedTaskIdsAndUpdateValue(setTaskSpeed, value);
  const setRetractSpeed = (value) => getAffectedTaskIdsAndUpdateValue(setTaskRetractSpeed, value);
  const setEnableMixing = (value) => getAffectedTaskIdsAndUpdateValue(setTaskEnableMixing, value);
  const setMixingVolume = (value) => getAffectedTaskIdsAndUpdateValue(setTaskMixingVolume, value);
  const setMixingCycle = (value) => getAffectedTaskIdsAndUpdateValue(setTaskMixingCycle, value);
  const setMixingSpeed = (value) => getAffectedTaskIdsAndUpdateValue(setTaskMixingSpeed, value);
  const setMixingPositionAspirate = (value) => getAffectedTaskIdsAndUpdateValue(setTaskMixingPositionAspirate, value);
  const setMixingPositionDispense = (value) => getAffectedTaskIdsAndUpdateValue(setTaskMixingPositionDispense, value);
  const setEnableTipTouchOff = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOff, value, taskType);
  const setTipTouchOffDelay = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffDelay, value, taskType);
  const setTipTouchOffHeight = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffHeight, value, taskType);
  const setTipTouchOffDirectionX = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffDirectionX, value, taskType);
  const setTipTouchOffDirectionY = (value, taskType) =>
    getAffectedTaskIdsAndUpdateValue(setTaskTipTouchOffDirectionY, value, taskType);

  const setBothMixingPosition = (value) => {
    batch(async () => {
      await setMixingPositionAspirate(value);
      await setMixingPositionDispense(value);
    });
  };

  return (
    <div>
      {isPipettingProfileExpanded && (
        <PipettingProfileWrapper>
          <PipettingProfileItems.TipPosition
            task={sampleAspirateTask}
            onChange={setTipPosition}
            onXTipPositionOnChange={setTipXPosition}
            onYTipPositionOnChange={setTipYPosition}
          />
          <PipettingProfileItems.Speed task={sampleAspirateTask} onChange={setSpeed} />
          <PipettingProfileItems.RetractSpeed task={sampleAspirateTask} onChange={setRetractSpeed} />
          <PipettingProfileItems.EnableMixing task={sampleAspirateTask} onChange={setEnableMixing} />
          <PipettingProfileItems.MixingDetailGroup
            task={sampleAspirateTask}
            onChangeMixingPosition={setBothMixingPosition}
            onChangeMixingPositionAspirate={setMixingPositionAspirate}
            onChangeMixingPositionDispense={setMixingPositionDispense}
            onChangeCycle={setMixingCycle}
            onChangeVolume={setMixingVolume}
            onChangeMixingSpeed={setMixingSpeed}
          />
          <PipettingProfileItems.TipTouchOff
            task={sampleAspirateTask}
            onChangeSwitch={setEnableTipTouchOff}
            onChangeDelay={setTipTouchOffDelay}
            onChangeHeight={setTipTouchOffHeight}
            onChangeDirectionX={setTipTouchOffDirectionX}
            onChangeDirectionY={setTipTouchOffDirectionY}
          />
        </PipettingProfileWrapper>
      )}
      <PipettingProfileExpanderButton isExpanded={isPipettingProfileExpanded} onClick={setIsPipettingProfileExpanded} />
    </div>
  );
}

SerialDilutionAliquotPipettingProfile.propTypes = {
  tasks: PropTypes.object.isRequired,
  runtimeMode: PropTypes.bool,
};

SerialDilutionAliquotPipettingProfile.defaultProps = {
  runtimeMode: false,
};

export default SerialDilutionAliquotPipettingProfile;
